import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [authType, setAuthType] = useState(null); // 'jwt' or 'apikey'
  const [loading, setLoading] = useState(true);

  // Load user from localStorage on mount
  useEffect(() => {
    const loadAuth = async () => {
      const accessToken = localStorage.getItem('accessToken');
      const apiKey = localStorage.getItem('apiKey');

      if (accessToken) {
        try {
          const response = await authAPI.getCurrentUser();
          setUser(response.data);
          setPermissions(response.data.permissions || []);
          setAuthType('jwt');
        } catch (error) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('currentUser');
          localStorage.removeItem('permissions');
        }
      } else if (apiKey) {
        setUser({ 
          username: 'API Key User',
          role: 'super_admin',
          full_name: 'System Administrator'
        });
        setPermissions(['*']);
        setAuthType('apikey');
      }

      setLoading(false);
    };

    loadAuth();
  }, []);

  const hasPermission = useCallback((permission) => {
    if (!user) return false;
    if (authType === 'apikey') return true;
    if (user.role === 'super_admin') return true;
    if (permissions.includes('*')) return true;
    if (user.role === 'admin') return true;
    return permissions.includes(permission);
  }, [user, permissions, authType]);

  const hasAnyPermission = useCallback((permissionList) => {
    if (!user) return false;
    if (authType === 'apikey' || user.role === 'super_admin') return true;
    if (permissions.includes('*')) return true;
    return permissionList.some(p => permissions.includes(p));
  }, [user, permissions, authType]);

  const hasRole = useCallback((role) => {
    if (!user) return false;
    if (authType === 'apikey') return true;
    return user.role === role;
  }, [user, authType]);

  const hasAnyRole = useCallback((roles) => {
    if (!user) return false;
    if (authType === 'apikey') return true;
    return roles.includes(user.role);
  }, [user, authType]);

  const login = useCallback((authResult) => {
    if (authResult.type === 'jwt') {
      setUser(authResult.user);
      setPermissions(authResult.permissions || []);
      setAuthType('jwt');
    } else {
      setUser({ 
        username: 'API Key User',
        role: 'super_admin',
        full_name: 'System Administrator'
      });
      setPermissions(['*']);
      setAuthType('apikey');
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      if (authType === 'jwt') {
        await authAPI.logout();
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('currentUser');
      localStorage.removeItem('permissions');
      localStorage.removeItem('apiKey');
      
      setUser(null);
      setPermissions([]);
      setAuthType(null);
    }
  }, [authType]);

  const value = {
    user,
    permissions,
    authType,
    loading,
    login,
    logout,
    hasPermission,
    hasAnyPermission,
    hasRole,
    hasAnyRole,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === 'super_admin' || authType === 'apikey',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;